import React from "react";
import Footer from "../Footer";

const sections = [
  {
    id: "site-guidelines",
    title: "1. Site Guidelines",
    content: [
      "Tutor Network BD is built to make private tuition discovery safer, easier, and more transparent for tutors, students, and guardians. All users must use the platform honestly, respectfully, and lawfully.",
    ],
    children: [
      {
        title: "1.1 For Tutors",
        paragraphs: [
          "Tutors must provide accurate profile information, including name, university/institution, academic background, subject expertise, location, contact details, and other required information. Tutors may not use fake names, false academic claims, fake certificates, misleading photos, or another person’s identity.",
          "Only verified or approved tutors may apply to tuition posts where verification is required. Tutor Network BD may request ID, student ID, university information, phone verification, or other supporting documents before approving or continuing a tutor profile.",
          "Tutors must behave professionally with guardians, students, tuition posters, and Tutor Network BD admins. Harassment, abusive language, pressure tactics, misleading promises, or inappropriate communication are strictly prohibited.",
          "Tutors must not bypass Tutor Network BD’s confirmation and commission process after receiving a tuition opportunity through the platform. If a tuition is confirmed through Tutor Network BD, the tutor must inform Tutor Network BD honestly and pay the applicable commission according to these Terms.",
        ],
      },
      {
        title: "1.2 For Guardians, Students, and Tuition Posters",
        paragraphs: [
          "Guardians, students, and tuition posters must provide accurate tuition information, including class, subject, location, preferred tutor gender, salary range, schedule, and contact details. Fake tuition posts, misleading salary offers, duplicate posts, or posts made only to collect tutor information are not allowed.",
          "Tutor Network BD does not charge guardians or students for posting tuition or contacting tutors unless a specific paid service is clearly introduced in the future. At present, the main monetization model is tutor-side.",
          "Guardians and students are responsible for final tutor selection, discussion, trial class decisions, payment of salary to the tutor, and household/student safety arrangements.",
        ],
      },
      {
        title: "1.3 General User Conduct",
        paragraphs: ["Users must not:"],
        bullets: [
          "Create fake accounts or impersonate others.",
          "Upload false, illegal, harmful, abusive, obscene, discriminatory, or misleading content.",
          "Share another person’s private information without permission.",
          "Use the platform for fraud, spam, phishing, harassment, or illegal activity.",
          "Scrape, copy, resell, or misuse Tutor Network BD’s data.",
          "Attempt to hack, overload, damage, or interfere with the website.",
          "Circumvent platform rules, payment obligations, verification systems, or admin decisions.",
        ],
        after: [
          "Tutor Network BD may suspend, restrict, or permanently remove any account, tuition post, application, package access, or profile that violates these Guidelines.",
        ],
      },
    ],
  },
  {
    id: "terms",
    title: "2. Terms & Conditions",
    children: [
      {
        title: "2.1 Nature of the Platform",
        paragraphs: [
          "Tutor Network BD is an online marketplace and connection platform. We help tutors, students, guardians, and tuition posters find and communicate with each other. We are not the employer of tutors, and we do not guarantee any tutor’s performance, any guardian’s payment behavior, or any tuition confirmation.",
          "A tuition arrangement is made directly between the tutor and the guardian/student/tuition poster. Tutor Network BD may assist with verification, communication, tuition posting, tutor applications, and confirmation checking, but the final agreement remains between the relevant users.",
        ],
      },
      {
        title: "2.2 Account Registration",
        paragraphs: [
          "To use certain features, users may need to create an account. You agree to provide accurate and updated information. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.",
          "Tutor Network BD may approve, reject, verify, suspend, or delete accounts at its discretion if we find suspicious, incomplete, misleading, abusive, or policy-violating activity.",
        ],
      },
      {
        title: "2.3 Tutor Verification",
        paragraphs: [
          "Tutor verification may include checking phone numbers, student IDs, institution names, profile details, location, photos, or other documents. Verification does not mean Tutor Network BD guarantees a tutor’s teaching quality, character, academic result, future conduct, or suitability for every student.",
          "Guardians and students should still conduct their own checks, interviews, trial classes, and safety assessments before finalizing any tutor.",
        ],
      },
      {
        title: "2.4 Tuition Board and Applications",
        paragraphs: [
          "Verified tutors may apply to tuition posts through the Tuition Board by using application credits. Tutor Network BD may provide free initial credits and may offer paid credit packages.",
          "A tuition post may become inactive after receiving a limited number of tutor applications, or after admin review, confirmation, expiry, or other operational reasons. Tutor Network BD may manually activate, deactivate, edit, remove, or review tuition posts to maintain platform quality.",
          "Applying to a tuition post does not guarantee selection, contact, trial class, or tuition confirmation.",
        ],
      },
      {
        title: "2.5 Tutor Board",
        paragraphs: [
          "The Tutor Board allows guardians and students to browse tutor profiles and contact tutors directly where available. Tutor Network BD may display selected tutor information such as name, institution, subjects, preferred location, experience, salary expectation, profile photo, and contact option.",
          "Tutor Network BD may edit, hide, rank, approve, or remove tutor profiles for quality, safety, or policy reasons.",
        ],
      },
      {
        title: "2.6 Packages, Credits, and Payments",
        paragraphs: [
          "Tutor Network BD may provide tutors with a limited number of free application credits. After using free credits, tutors may purchase paid packages to apply to more tuition posts.",
          "Unless clearly stated otherwise, paid packages are for application access only. Purchasing a package does not guarantee tuition confirmation, guardian response, interview, trial class, or job placement.",
          "Package fees are generally non-refundable after credits are added or used, except where Tutor Network BD decides otherwise due to technical error, duplicate payment, failed credit delivery, or another valid reason.",
          "Tutor Network BD may change package prices, credit rules, validity periods, and package features from time to time. Any changes will apply prospectively unless otherwise stated.",
        ],
      },
      {
        title: "2.7 Tuition Confirmation and Commission",
        paragraphs: [
          "Tutor Network BD checks tuition confirmation with the guardian/student/tuition poster where applicable.",
          "If a tutor receives and confirms a tuition through Tutor Network BD, the tutor must pay Tutor Network BD 50% of the first month’s salary as service commission within 7 days of tuition confirmation.",
          "For example, if the confirmed first month salary is BDT 8,000, the applicable commission is BDT 4,000.",
          "The commission becomes payable only after tuition confirmation. However, once confirmed, the tutor must not avoid, delay, hide, misrepresent, or bypass the commission obligation.",
          "A tuition may be considered confirmed if the tutor and guardian/student agree to start tuition, the tutor attends class, the tutor receives a final schedule, or there is other reasonable evidence that the tuition has started or been accepted.",
          "Failure to pay the commission may result in account suspension, loss of access to packages or applications, removal from Tutor Board, restriction from future tuition opportunities, and other lawful recovery steps.",
        ],
      },
      {
        title: "2.8 No Guardian Commission",
        paragraphs: [
          "Tutor Network BD does not currently charge guardians or students any commission for hiring tutors through the platform. The service commission is charged to tutors after successful tuition confirmation, as described above.",
        ],
      },
      {
        title:
          "2.9 Salary, Trial Class, and Payment Between Tutor and Guardian",
        paragraphs: [
          "Tutor salary, payment date, teaching schedule, number of classes, trial class policy, transport cost, online/offline mode, and other tuition conditions must be discussed and agreed between the tutor and guardian/student.",
          "Tutor Network BD is not responsible for unpaid salary, salary disputes, missed classes, guardian cancellation, tutor cancellation, student dissatisfaction, or personal disagreements, although we may assist in resolving complaints where possible.",
        ],
      },
      {
        title: "2.10 User Safety",
        paragraphs: [
          "For offline tuition, users should take reasonable safety precautions. Guardians should verify tutor identity before allowing home visits. Tutors should confirm location, guardian identity, and tuition details before visiting.",
          "Tutor Network BD may remove users who behave suspiciously, abusively, fraudulently, or dangerously. However, users remain responsible for their own judgment and safety when communicating or meeting outside the website.",
        ],
      },
      {
        title: "2.11 Platform Availability",
        paragraphs: [
          "We try to keep Tutor Network BD available and functional, but we do not guarantee uninterrupted access. The website may be unavailable due to maintenance, technical issues, hosting problems, security events, or updates.",
          "We may update, modify, suspend, or discontinue any feature of the website at any time.",
        ],
      },
      {
        title: "2.12 Limitation of Liability",
        paragraphs: [
          "To the maximum extent permitted by law, Tutor Network BD is not liable for indirect, incidental, special, or consequential losses arising from use of the platform, including loss of income, failed tuition confirmation, user misconduct, inaccurate tuition information, unpaid salary, personal disputes, or third-party actions.",
          "Our role is limited to operating the platform, facilitating discovery, managing applications, supporting verification, and assisting communication.",
        ],
      },
      {
        title: "2.13 Account Suspension or Termination",
        paragraphs: [
          "Tutor Network BD may suspend or terminate an account if the user violates these Terms, provides false information, misuses platform data, avoids commission, harasses others, posts fake tuition, engages in fraud, or harms the platform’s trust and safety.",
          "Users may request account deletion by contacting us at support@goatteachers.com.",
        ],
      },
      {
        title: "2.14 Changes to These Terms",
        paragraphs: [
          "We may update these Site Guidelines, Terms & Conditions, Privacy Policy, and Copyright Compliance Policy from time to time. Updated versions will be posted on the website with a revised effective date. Continued use of the website after changes means you accept the updated terms.",
        ],
      },
      {
        title: "2.15 Governing Law",
        paragraphs: [
          "These Terms are governed by the laws of Bangladesh. Any dispute shall be handled under the applicable laws and courts of Bangladesh, subject to any mandatory legal requirements.",
        ],
      },
    ],
  },
  {
    id: "privacy",
    title: "3. Privacy Policy",
    content: [
      "Tutor Network BD respects user privacy and aims to collect only the information needed to operate a safe and functional tutor marketplace. Bangladesh’s Personal Data Protection Act, 2026 recognizes personal data protection, consent-based lawful processing, data subject rights, confidentiality, security, retention requirements, breach handling, and accountability obligations. (Bangladesh Laws)",
    ],
    children: [
      {
        title: "3.1 Information We Collect",
        paragraphs: ["We may collect the following types of information:"],
        detailList: [
          [
            "Account Information",
            "name, phone number, email address, password/login information, role type, and account status.",
          ],
          [
            "Tutor Profile Information",
            "photo, gender, university/institution, department, academic background, subject expertise, preferred class level, preferred area, expected salary, teaching experience, bio, and verification information.",
          ],
          [
            "Guardian/Student/Tuition Information",
            "student name, class, medium, subjects, location, salary range, preferred tutor gender, tuition schedule, phone number, WhatsApp number, and tuition requirements.",
          ],
          [
            "Verification Information",
            "student ID, institutional information, supporting documents, admin notes, and communication records used to verify users.",
          ],
          [
            "Payment Information",
            "package purchase history, transaction ID, payment method, payment amount, commission status, due amount, and related billing records.",
          ],
          [
            "Usage and Technical Information",
            "IP address, browser type, device information, login activity, website activity, cookies, error logs, and security logs.",
          ],
          [
            "Communication Information",
            "messages, calls, WhatsApp-related details, admin support requests, complaints, feedback, and dispute records where submitted to us.",
          ],
        ],
      },
      {
        title: "3.2 How We Use Information",
        paragraphs: ["We use information to:"],
        bullets: [
          "Create and manage user accounts.",
          "Verify tutor profiles.",
          "Display tutor profiles on the Tutor Board.",
          "Publish and manage tuition posts.",
          "Allow tutors to apply to tuition posts.",
          "Connect tutors with guardians/students.",
          "Check tuition confirmation.",
          "Manage packages, credits, payments, and commission.",
          "Prevent fraud, fake accounts, harassment, and misuse.",
          "Improve website features and user experience.",
          "Provide customer support.",
          "Comply with legal, regulatory, security, and administrative requirements.",
        ],
      },
      {
        title: "3.3 Legal Basis for Processing",
        paragraphs: [
          "We process personal information where it is necessary for account creation, platform operation, user consent, legitimate platform safety, payment management, verification, legal compliance, dispute resolution, or service improvement.",
        ],
      },
      {
        title: "3.4 Sharing of Information",
        paragraphs: [
          "We may share relevant user information in the following situations:",
        ],
        bullets: [
          "Tutor profile information may be visible to guardians/students on the Tutor Board.",
          "Tuition post information may be visible to eligible tutors.",
          "Contact information may be shared when a user applies, contacts, or uses relevant platform features.",
          "Payment or transaction information may be shared with payment service providers where necessary.",
          "Information may be shared with legal authorities if required by law or to protect users, the platform, or public safety.",
          "Information may be shared with service providers who help us operate hosting, analytics, SMS, email, verification, payment, or support systems.",
        ],
        after: ["We do not sell user personal information to advertisers."],
      },
      {
        title: "3.5 Cookies and Tracking",
        paragraphs: [
          "Tutor Network BD may use cookies or similar technologies to keep users logged in, improve security, remember preferences, analyze traffic, and improve platform performance. Users may control cookies through browser settings, but some features may not work properly if cookies are disabled.",
        ],
      },
      {
        title: "3.6 Data Security",
        paragraphs: [
          "We use reasonable technical and organizational measures to protect user data from unauthorized access, misuse, loss, alteration, or disclosure. However, no online platform is completely risk-free. Users should keep login credentials confidential and report suspicious activity immediately.",
          "Bangladesh’s cyber legal framework includes rules dealing with unlawful access, hacking, cyber fraud, deception, and other cyber offences, so users must not misuse the platform or attempt unauthorized access. (Bangladesh Laws)",
        ],
      },
      {
        title: "3.7 Data Retention",
        paragraphs: [
          "We keep personal information only as long as necessary for platform operation, verification, legal compliance, payment records, fraud prevention, dispute resolution, and business records.",
          "We may retain certain records even after account deletion if needed for legal, security, accounting, or dispute-related purposes.",
        ],
      },
      {
        title: "3.8 User Rights",
        paragraphs: [
          "Subject to applicable law and verification, users may request to access, correct, update, or delete their personal information. Users may also request withdrawal of consent where processing is based on consent, although this may limit access to some services.",
          "Requests can be sent to: support@goatteachers.com",
        ],
      },
      {
        title: "3.9 Children and Minors",
        paragraphs: [
          "Tutor Network BD is designed for guardians, students, and tutors. If a student is a minor, a guardian should supervise use of the platform and communication with tutors. We do not knowingly collect unnecessary personal information from minors without appropriate guardian involvement.",
        ],
      },
      {
        title: "3.10 Cross-Border Services",
        paragraphs: [
          "Some hosting, analytics, email, or technical service providers may process data outside Bangladesh. Where applicable, we will try to use reasonable safeguards and comply with relevant legal requirements.",
        ],
      },
      {
        title: "3.11 Privacy Updates",
        paragraphs: [
          "We may update this Privacy Policy from time to time. The updated version will be posted on the website with a revised effective date.",
        ],
      },
    ],
  },
  {
    id: "copyright",
    title: "4. Copyright and Intellectual Property Compliance",
    content: [
      "Tutor Network BD respects copyright, trademarks, and other intellectual property rights. Bangladesh’s Copyright Act, 2000 protects original works and governs copyright-related rights and remedies in Bangladesh. (Bangladesh Trade Portal)",
    ],
    children: [
      {
        title: "4.1 Ownership of Tutor Network BD Content",
        paragraphs: [
          "All website design, branding, logos, text, graphics, icons, software, layout, features, databases, and platform content created by Tutor Network BD are owned by or licensed to Tutor Network BD, unless otherwise stated.",
          "Users may not copy, reproduce, modify, scrape, sell, distribute, or commercially exploit Tutor Network BD content without written permission.",
        ],
      },
      {
        title: "4.2 User-Submitted Content",
        paragraphs: [
          "Users may upload or submit profile information, photos, tuition posts, descriptions, messages, documents, or other content. By submitting content, users confirm that:",
        ],
        bullets: [
          "The content is accurate and lawful.",
          "They own the content or have permission to use it.",
          "The content does not violate copyright, privacy, trademark, or other rights of any person.",
          "The content does not contain false, harmful, abusive, obscene, discriminatory, or illegal material.",
        ],
        after: [
          "By submitting content to Tutor Network BD, users grant Tutor Network BD a non-exclusive, royalty-free, limited license to use, display, store, reproduce, modify, and distribute that content only as needed to operate, promote, secure, and improve the platform.",
        ],
      },
      {
        title: "4.3 Prohibited Copyright Activities",
        paragraphs: ["Users must not upload or share:"],
        bullets: [
          "Copyrighted books, notes, PDFs, slides, images, videos, or other materials without permission.",
          "Another person’s photo, certificate, ID, or document without consent.",
          "Copied tutor profiles or tuition posts from another platform without authorization.",
          "Branding, logos, or materials that mislead users or impersonate another organization.",
        ],
      },
      {
        title: "4.4 Copyright Complaint Procedure",
        paragraphs: [
          "If you believe content on Tutor Network BD violates your copyright or intellectual property rights, please contact us at support@goatteachers.com with:",
        ],
        bullets: [
          "Your name and contact information.",
          "Identification of the copyrighted work.",
          "The URL or location of the allegedly infringing content.",
          "A short explanation of why you believe the content infringes your rights.",
          "Proof of ownership or authorization, if available.",
          "A statement that the information you provided is accurate.",
        ],
        after: [
          "After receiving a valid complaint, Tutor Network BD may remove, restrict, or disable the disputed content and may contact the user who uploaded it.",
        ],
      },
      {
        title: "4.5 Repeat Infringers",
        paragraphs: [
          "Users who repeatedly upload infringing, misleading, or unauthorized content may have their accounts suspended or permanently removed.",
        ],
      },
      {
        title: "4.6 Platform Name and Branding",
        paragraphs: [
          "“Tutor Network BD,” “Tutor Network,” “Goat Teachers,” related logos, design elements, slogans, and platform branding may not be used without written permission, except where allowed by law.",
        ],
      },
    ],
  },
  {
    id: "contact",
    title: "5. Complaints, Support, and Contact",
    content: [
      "For account issues, tuition complaints, verification questions, privacy requests, copyright complaints, payment issues, or commission-related matters, contact:",
    ],
    contact: {
      title: "Tutor Network BD Support",
      email: "support@goatteachers.com",
      phone: "+8801824796093",
      address: "Dhaka 1216",
    },
    contentAfter: [
      "We may ask for verification before taking action on privacy, payment, account, or copyright-related requests.",
    ],
  },
  {
    id: "declaration",
    title: "6. Final Declaration",
    content: [
      "By using Tutor Network BD, you confirm that you have read, understood, and agreed to these Site Guidelines, Terms & Conditions, Privacy Policy, and Copyright Compliance Policy. You also agree to use the platform honestly, safely, respectfully, and in accordance with the laws of Bangladesh.",
    ],
  },
];

const navItems = [
  { href: "#site-guidelines", label: "Site Guidelines" },
  { href: "#terms", label: "Terms" },
  { href: "#privacy", label: "Privacy" },
  { href: "#copyright", label: "Copyright" },
  { href: "#contact", label: "Contact" },
];

function Paragraph({ children }) {
  return (
    <p className="mt-3 text-sm md:text-base leading-7 text-slate-600">
      {children}
    </p>
  );
}

function BulletList({ items }) {
  if (!items?.length) return null;

  return (
    <ul className="mt-4 space-y-2 pl-5 text-sm md:text-base leading-7 text-slate-600 list-disc">
      {items.map((item, index) => (
        <li key={index}>{item}</li>
      ))}
    </ul>
  );
}

function DetailList({ items }) {
  if (!items?.length) return null;

  return (
    <div className="mt-5 grid gap-3">
      {items.map(([label, value]) => (
        <div
          key={label}
          className="rounded-2xl border border-slate-200 bg-slate-50 p-4"
        >
          <p className="text-sm font-semibold text-slate-900">{label}</p>
          <p className="mt-1 text-sm leading-6 text-slate-600">{value}</p>
        </div>
      ))}
    </div>
  );
}

function ChildSection({ child }) {
  return (
    <div className="mt-7 border-t border-slate-100 pt-6 first:mt-0 first:border-t-0 first:pt-0">
      <h3 className="text-lg md:text-xl font-semibold text-slate-900">
        {child.title}
      </h3>

      {child.paragraphs?.map((paragraph, index) => (
        <Paragraph key={index}>{paragraph}</Paragraph>
      ))}

      <BulletList items={child.bullets} />
      <DetailList items={child.detailList} />

      {child.after?.map((paragraph, index) => (
        <Paragraph key={`after-${index}`}>{paragraph}</Paragraph>
      ))}
    </div>
  );
}

function LegalSection({ section }) {
  return (
    <section
      id={section.id}
      className="scroll-mt-28 rounded-3xl border border-slate-200 bg-white p-6 md:p-8 shadow-sm"
    >
      <h2 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-950">
        {section.title}
      </h2>

      {section.content?.map((paragraph, index) => (
        <Paragraph key={index}>{paragraph}</Paragraph>
      ))}

      {section.children?.map((child) => (
        <ChildSection key={child.title} child={child} />
      ))}

      {section.contact && (
        <div className="mt-6 rounded-2xl bg-slate-950 p-5 text-white">
          <p className="text-lg font-semibold">{section.contact.title}</p>
          <div className="mt-3 space-y-1 text-sm leading-6 text-slate-200">
            <p>Email: {section.contact.email}</p>
            <p>Phone/WhatsApp: {section.contact.phone}</p>
            <p>Address: {section.contact.address}</p>
          </div>
        </div>
      )}

      {section.contentAfter?.map((paragraph, index) => (
        <Paragraph key={`content-after-${index}`}>{paragraph}</Paragraph>
      ))}
    </section>
  );
}

export default function Legal() {
  return (
    <>
      <main className="min-h-screen bg-slate-50 text-slate-900">
        <section className="relative overflow-hidden bg-slate-950 px-4 py-16 text-white sm:px-6 lg:px-8">
          <div className="absolute inset-0 opacity-20">
            <div className="absolute -top-24 right-10 h-72 w-72 rounded-full bg-emerald-400 blur-3xl" />
            <div className="absolute bottom-0 left-0 h-72 w-72 rounded-full bg-sky-400 blur-3xl" />
          </div>

          <div className="relative mx-auto max-w-6xl">
            <span className="inline-flex rounded-full border border-white/10 bg-white/10 px-4 py-1.5 text-sm font-medium text-emerald-100">
              Legal Information
            </span>

            <h1 className="mt-5 max-w-4xl text-4xl font-bold tracking-tight md:text-6xl">
              Tutor Network BD Site Guidelines, Terms, Privacy Policy &
              Copyright Compliance
            </h1>

            <div className="mt-7 grid gap-3 text-sm text-slate-200 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-slate-400">Effective Date</p>
                <p className="mt-1 font-semibold text-white">01-06-2026</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-slate-400">Website</p>
                <p className="mt-1 font-semibold text-white">
                  tn.goatteachers.com
                </p>
                <p className="font-semibold text-white">tn.goatteachers.com</p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-slate-400">Operated By</p>
                <p className="mt-1 font-semibold text-white">
                  Goat Technologies
                </p>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/10 p-4">
                <p className="text-slate-400">Contact</p>
                <p className="mt-1 font-semibold text-white">+8801824796093</p>
              </div>
            </div>

            <p className="mt-8 max-w-4xl text-base leading-8 text-slate-200 md:text-lg">
              Tutor Network BD is a Bangladesh-focused online tutor marketplace
              that connects verified tutors with students, guardians, and
              tuition posters. By using our website, creating an account,
              applying to tuition posts, posting tuition requirements,
              purchasing packages, or contacting users through Tutor Network BD,
              you agree to the following Site Guidelines, Terms & Conditions,
              Privacy Policy, and Copyright Compliance Policy.
            </p>
          </div>
        </section>

        <div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:px-6 lg:grid-cols-[260px_1fr] lg:px-8">
          <aside className="lg:sticky lg:top-24 lg:self-start">
            <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                On this page
              </p>
              <nav className="mt-4 space-y-2">
                {navItems.map((item) => (
                  <a
                    key={item.href}
                    href={item.href}
                    className="block rounded-xl px-3 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-100 hover:text-slate-950"
                  >
                    {item.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          <div className="space-y-8">
            {sections.map((section) => (
              <LegalSection key={section.id} section={section} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
